import { DetailViewModel } from "@frui.ts/datascreens";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { hasVisibleErrors, validate } from "@frui.ts/validation";
import { computed } from "mobx";
import type { EntityNames } from "../config";
import type { ICreateRepository } from "../repositories/types";
import type { CreatedEvent } from "../services/eventBus";
import type EventBus from "../services/eventBus";
import type LocalizationService from "../services/localizationService";
import type NotificationService from "../services/notificationService";
import { NotificationType } from "../services/notificationService";

export default abstract class CreateViewModel<TEntity> extends DetailViewModel<TEntity> {
  protected abstract readonly modelName: EntityNames;
  protected abstract localizationService: LocalizationService;
  protected abstract repository: ICreateRepository<TEntity>;
  protected abstract notificationService: NotificationService;
  protected abstract eventBus: EventBus;

  abstract translate(code: string, size?: number): string;

  @computed
  get isEditable() {
    return true;
  }

  @bound
  protected onCreated(item?: TEntity) {
    this.notificationService.add({ title: this.translate("created"), type: NotificationType.SUCCESS });
    this.eventBus.publish<CreatedEvent<TEntity>>({ type: `${this.modelName}.created`, payload: { [this.modelName]: item } });
    void this.requestClose();
  }

  @computed
  get canSave() {
    return !hasVisibleErrors(this.item);
  }

  protected postData() {
    return this.repository.create(this.item);
  }

  @bound
  @watchBusy
  async create() {
    if (validate(this.item)) {
      try {
        const item = await this.postData();
        this.onCreated(item);
      } catch (e) {}
    }
  }

  @bound
  cancel() {
    void this.requestClose();
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateAttribute(attribute: PropertyName<TEntity>, size?: number) {
    return this.localizationService.translateAttribute(this.modelName, attribute, size);
  }
}
